// Minnesotaänger Mini App — v1 global state.
// New code (not from telegram-tt). The v1 store keeps conversations and
// messages separate from the (unused) telegram-tt state tree that remains in
// GlobalState for structural compatibility.

import type { ApiMiniAppConversation, ApiMiniAppMessage } from '../../api/miniapp/types';

// A mutation accepted with 202 but not yet observable in the history
// (CHAT_CONTRACT §3.4/§5). Tracked until the completion predicate holds or
// the deadline passes; polling (actions/api/polling.ts) drives the lifecycle.
export interface MiniAppPendingOperation {
  kind: 'send' | 'reroll' | 'edit' | 'delete';
  // reroll/edit/delete: the message they rewrite; null for send.
  targetEventId: number | null;
  // Max event_id in the chat when the op was registered — completion baseline
  // for send (an assistant message with a greater event_id = reply arrived).
  tailEventId: number;
  startedAt: number;
}

export interface MiniAppGlobalState {
  conversations: ApiMiniAppConversation[];
  isLoadingConversations: boolean;
  // Non-null when the last loadConversations attempt failed. The UI must show
  // this instead of the empty-list state ("暂无会话"), which only means a
  // successful request returned zero conversations.
  conversationsError: string | null;

  // Per-chat message arrays, newest last (rendering order).
  messagesByChatId: Record<string, ApiMiniAppMessage[]>;
  // Pagination: cursor for "older" fetch; null means the backend has no more.
  olderCursorByChatId: Record<string, number | null>;
  isLoadingMessagesChatIds: string[];
  isLoadingMoreChatIds: string[];

  activeChatId?: string;

  // Local-only UI state (not backend-backed).
  sendingChatIds: string[];
  sendFailedByChatId: Record<string, string | null>;
  selectedMessageIdsByChatId: Record<string, string[]>;
  // v1 (fix-list E): reply target is the stable event_id (l0 rowid).
  replyingToEventIdByChatId: Record<string, number>;
  // Composer text is shared state (CHAT_CONTRACT §3.3): cleared only when a
  // send is accepted (202), so a failed send keeps its text for retry.
  composerTextByChatId: Record<string, string>;
  // 202-accepted mutations awaiting worker completion (CHAT_CONTRACT §3.4).
  pendingOperationsByChatId: Record<string, MiniAppPendingOperation[]>;
  dialog: {
    title?: string;
    message: string;
  } | null;
}

export function createInitialMiniAppState(): MiniAppGlobalState {
  return {
    conversations: [],
    isLoadingConversations: false,
    conversationsError: null,
    messagesByChatId: {},
    olderCursorByChatId: {},
    isLoadingMessagesChatIds: [],
    isLoadingMoreChatIds: [],
    sendingChatIds: [],
    sendFailedByChatId: {},
    selectedMessageIdsByChatId: {},
    replyingToEventIdByChatId: {},
    composerTextByChatId: {},
    pendingOperationsByChatId: {},
    dialog: null,
  };
}
